import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Starluck.css';

gsap.registerPlugin(ScrollTrigger);

function Starluck() {
  const cardsRef = useRef([]);
  const heroRef = useRef(null);
  const heroImageRef = useRef(null);
  const heroTextRef = useRef(null);

  useEffect(() => {
    const cleanupFunctions = [];
    const scrollTriggers = [];

    // Hero Parallax on Mouse Move
    if (heroRef.current && heroImageRef.current && heroTextRef.current) {
      const hero = heroRef.current;
      
      const handleMouseMove = (e) => {
        const { clientX, clientY } = e;
        const { offsetWidth, offsetHeight } = hero;
        const xPos = (clientX / offsetWidth - 0.5) * 30;
        const yPos = (clientY / offsetHeight - 0.5) * 30;

        gsap.to(heroImageRef.current, { duration: 1, x: -xPos, y: -yPos, ease: 'power3.out' });
        gsap.to(heroTextRef.current, { duration: 1, x: xPos, y: yPos, ease: 'power3.out' });
      };

      const handleMouseLeave = () => {
        gsap.to([heroImageRef.current, heroTextRef.current], { 
          duration: 1, 
          x: 0, 
          y: 0, 
          ease: 'elastic.out(1, 0.5)' 
        });
      };

      hero.addEventListener('mousemove', handleMouseMove);
      hero.addEventListener('mouseleave', handleMouseLeave);

      cleanupFunctions.push(() => {
        hero.removeEventListener('mousemove', handleMouseMove);
        hero.removeEventListener('mouseleave', handleMouseLeave);
      });

      // Hero scroll animation
      const heroAnimation = gsap.fromTo([heroImageRef.current, heroTextRef.current],
        { autoAlpha: 0, y: 60, scale: 0.95 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 1,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      if (heroAnimation.scrollTrigger) {
        scrollTriggers.push(heroAnimation.scrollTrigger);
      }
    }

    // Interactive Card Tilt Effect
    const cards = cardsRef.current.filter(Boolean);
    
    cards.forEach(card => {
      const handleMouseMove = (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const { width, height } = rect;

        const rotateX = gsap.utils.mapRange(0, height, -10, 10)(y);
        const rotateY = gsap.utils.mapRange(0, width, 10, -10)(x);

        gsap.to(card, {
          duration: 0.7,
          rotationX: rotateX,
          rotationY: rotateY,
          transformPerspective: 1000,
          ease: 'power2.out'
        });
      };

      const handleMouseLeave = () => {
        gsap.to(card, {
          duration: 1,
          rotationX: 0,
          rotationY: 0,
          ease: 'elastic.out(1, 0.5)'
        });
      };

      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);

      cleanupFunctions.push(() => {
        card.removeEventListener('mousemove', handleMouseMove);
        card.removeEventListener('mouseleave', handleMouseLeave);
      });
    });

    // Scroll animations for cards
    cards.forEach((card, index) => {
      const cardAnimation = gsap.fromTo(card,
        { autoAlpha: 0, y: 60, scale: 0.95 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 1,
          delay: index * 0.2,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      if (cardAnimation.scrollTrigger) {
        scrollTriggers.push(cardAnimation.scrollTrigger);
      }
    });

    // Cleanup
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
      scrollTriggers.forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section className="starluck-section scroll-fade-in">
      <div className="starluck-container">
        {/* Hero Section */}
        <div ref={heroRef} className="starluck-hero">
          <div className="starluck-hero-grid">
            {/* Image (2/3) */}
            <div ref={heroImageRef} className="starluck-hero-image-container">
              <img 
                src="/WebsitePhotos/starluck.jpg" 
                alt="Starluck" 
                className="starluck-hero-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/800x600/222222/FFFFFF?text=Image';
                }}
              />
            </div>
            {/* Text Content (1/3) */}
            <div ref={heroTextRef} className="starluck-hero-text-container">
              <h1 className="starluck-hero-title">
                Our Current Project: <span className="starluck-hero-accent">Starluck</span>
              </h1>
              <p className="starluck-hero-description">
                We are creating a <strong>mobile astrology app</strong> that combines ancient wisdom with modern technology. 
                Starluck focuses on precise <strong>luck node calculations</strong>, personalized <strong>horoscope readings</strong>, and 
                detailed <strong>celestial aspect analysis</strong>. Through advanced algorithms and intuitive design, 
                we're making complex astrological concepts accessible and actionable for everyone.
              </p>
            </div>
          </div>
        </div>

        {/* Cards Section */}
        <div className="starluck-grid">
          {/* Card 1 */}
          <div ref={el => cardsRef.current[0] = el} className="starluck-card starluck-card-dark">
            <h2 className="starluck-card-title">
              <span className="starluck-accent">Tech</span> Stack
            </h2>
            <p className="starluck-card-description">
              Built with <strong>React Native</strong> for seamless cross-platform mobile performance and <strong>FastAPI</strong>{" "}
              for robust backend services. The architecture ensures real-time astrological calculations, smooth user experiences, 
              and scalable infrastructure to handle complex celestial computations and personalized horoscope generation.
            </p>
          </div>

          {/* Card 2 (Yellow) */}
          <div ref={el => cardsRef.current[1] = el} className="starluck-card starluck-card-yellow">
            <div className="starluck-card-icon">
              <i className="fas fa-star"></i>
            </div>
            <h2 className="starluck-card-title-yellow">
              Design
            </h2>
            <p className="starluck-card-description-yellow">
              Starluck features a <strong>bold, minimalist design</strong> inspired by <strong>golden Egyptian aesthetics</strong>{" "}
              and ancient celestial art. The interface combines deep black backgrounds with golden accents, making complex 
              astrological data <strong>visually stunning</strong> and intuitive.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Starluck;

